import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useRef,
} from "react";
import { UserVideoWord } from "@shared_types";
import { isInvalidDictionaryWord } from "../Subtitles/invalidDictionaryWord.ts";
import { haptic } from "@/lib/haptic.ts";

interface WordHighlighterProps {
  word: UserVideoWord;
  allWords: UserVideoWord[];
  position: number;
  highlightPosition: number;
  isRightMostPhraseWord: boolean;
  isLeftMostPhraseWord: boolean;
  isPhraseMiddle: boolean;
  onWordClick: (word: UserVideoWord) => void;
  onDoubleWordClick: (word: UserVideoWord) => void;
  onPressDown: (word: UserVideoWord) => void;
  onPressMove: (word: UserVideoWord) => void;
  onPressUp: () => void;
}

const WordHighlighter: React.FC<WordHighlighterProps> = ({
  word,
  allWords,
  position,
  highlightPosition,
  onWordClick,
  onDoubleWordClick,
  isRightMostPhraseWord = false,
  isLeftMostPhraseWord = false,
  isPhraseMiddle = false,
  onPressDown = () => {},
  onPressMove = () => {},
  onPressUp = () => {},
}) => {
  const highlightColorBlue = "bg-[var(--color-blue)]";
  const [lastTap, setLastTap] = useState(0);

  const wordCls = () => {
    if (isInvalidDictionaryWord(word.displayText)) {
      return "bg-transparent";
    }
    return "bg-transparent";
  };

  const handleClick = () => {
    const currentTime = new Date().getTime();
    const tapGap = currentTime - lastTap;
    if (tapGap < 300 && tapGap > 0 && onDoubleWordClick) {
      haptic.light();
      onDoubleWordClick(word);
    } else {
      onWordClick(word);
    }
    setLastTap(currentTime);
  };

  let innerWordHighlightColor = "";

  if (position === highlightPosition) {
    innerWordHighlightColor = highlightColorBlue + " text-white";
  } else {
    innerWordHighlightColor = wordCls();
  }

  let spanColor = "bg-transparent";
  let spanRound = "rounded-md";

  if (isLeftMostPhraseWord || isRightMostPhraseWord || isPhraseMiddle) {
    spanColor = highlightColorBlue;
    innerWordHighlightColor = highlightColorBlue + " text-white";
  }

  if (isPhraseMiddle) {
    spanRound = "";
  } else if (isLeftMostPhraseWord) {
    spanRound = "rounded-l-md";
  } else if (isRightMostPhraseWord) {
    spanRound = "rounded-r-md";
  }

  return (
    <span
      className={`cursor-pointer ${spanColor + " " + spanRound}  
      selection:text-white inline-flex 
      items-center justify-center px-[2px] my-1`}
      key={word.videoWordId}
      onMouseDown={(e) => {
        e.preventDefault();
        onPressDown(word);
      }}
      onMouseEnter={() => {
        onPressMove(word);
      }}
      onTouchStart={() => {
        onPressDown(word);
      }}
      onTouchMove={(event: React.TouchEvent<HTMLSpanElement>) => {
        const dataPos = getPosFromTouchEvent(event);
        if (dataPos) {
          const wordUnderTouch = allWords[parseInt(dataPos)];
          onPressMove(wordUnderTouch);
        }
      }}
      onTouchEnd={() => {
        onPressUp();
      }}
    >
      <span
        data-pos={position}
        className={innerWordHighlightColor + " rounded-md px-[3px]"}
        onClick={handleClick}
      >
        {word.displayText}
      </span>{" "}
    </span>
  );
};

function getPosFromTouchEvent(event: React.TouchEvent<HTMLSpanElement>) {
  const touch = event.changedTouches[0];
  const x = touch.clientX;
  const y = touch.clientY;
  const elementUnderTouch = document.elementFromPoint(x, y);
  return elementUnderTouch?.getAttribute("data-pos");
}

interface PlacementTestHighlightedWordsProps {
  words: UserVideoWord[];
  highlightPosition: number;
  onWordClick: (word: UserVideoWord) => void;
  onDoubleWordClick?: (word: UserVideoWord) => void;
  onPhraseSelectedDone: (
    startWord: UserVideoWord,
    endWord: UserVideoWord
  ) => void;
  onPhraseSelectStart: () => void;
}

export interface PlacementTestHighlightedWordsRef {
  clearPhraseSelection: () => void;
  getParagraphRef: () => HTMLParagraphElement | null;
}

const PlacementTestHighlightedWords = forwardRef<
  PlacementTestHighlightedWordsRef,
  PlacementTestHighlightedWordsProps
>(
  (
    {
      words,
      highlightPosition,
      onWordClick,
      onDoubleWordClick,
      onPhraseSelectedDone,
      onPhraseSelectStart,
    },
    ref
  ) => {
    const [isDraggingPhrase, setIsDraggingPhrase] = useState(false);
    const [phraseStartIndex, setPhraseStartIndex] = useState<number | null>(null);
    const [phraseEndIndex, setPhraseEndIndex] = useState<number | null>(null);

    const paragraphRef = useRef<HTMLParagraphElement>(null);

    useImperativeHandle(ref, () => ({
      clearPhraseSelection: () => {
        setPhraseStartIndex(null);
        setPhraseEndIndex(null);
        setIsDraggingPhrase(false);
      },
      getParagraphRef: () => paragraphRef.current,
    }));

    const downHapticOnce = useRef(false);

    const handlePressDown = (word: UserVideoWord) => {
      const wordIndex = words.findIndex(
        (w) => w.videoWordId === word.videoWordId
      );
      if (!downHapticOnce.current) {
        haptic.selection();
        downHapticOnce.current = true;
        setTimeout(() => {
          downHapticOnce.current = false;
        }, 500);
      }
      setPhraseStartIndex(wordIndex);
      setPhraseEndIndex(wordIndex);
      setIsDraggingPhrase(true);
      onPhraseSelectStart();
      onWordClick(word);
    };

    const handlePressMove = (word: UserVideoWord) => {
      const wordIndex = words.findIndex(
        (w) => w.videoWordId === word.videoWordId
      );
      if (isDraggingPhrase) {
        setPhraseEndIndex(wordIndex);
      }
    };

    useEffect(() => {
      function handleGlobalMouseUp() {
        handlePressUp();
      }

      if (isDraggingPhrase) {
        document.addEventListener("mouseup", handleGlobalMouseUp);
      }
      return () => {
        document.removeEventListener("mouseup", handleGlobalMouseUp);
      };
    }, [
      isDraggingPhrase,
      phraseStartIndex,
      phraseEndIndex,
      words,
      onPhraseSelectedDone,
    ]);

    const upHapticOnce = useRef(false);

    const handlePressUp = () => {
      if (
        isDraggingPhrase &&
        phraseStartIndex !== null &&
        phraseEndIndex !== null
      ) {
        setIsDraggingPhrase(false);
        if (!upHapticOnce.current) {
          haptic.selection();
          upHapticOnce.current = true;
          setTimeout(() => {
            upHapticOnce.current = false;
          }, 500);
        }
        const startWord = words[phraseStartIndex];
        const endWord = words[phraseEndIndex];
        onPhraseSelectedDone(startWord, endWord);
      }
    };

    return (
      <p
        ref={paragraphRef}
        style={{
          lineHeight: "1.3",
        }}
      >
        {words.map((word, index) => {
          let isRightMostPhraseWord = false;
          let isLeftMostPhraseWord = false;
          let isPhraseMiddle = false;
          const isLengthOne = phraseStartIndex == phraseEndIndex;
          if (
            phraseStartIndex !== null &&
            phraseEndIndex !== null &&
            !isLengthOne
          ) {
            const leftMostIdx = Math.min(phraseStartIndex, phraseEndIndex);
            const rightMostIdx = Math.max(phraseStartIndex, phraseEndIndex);
            isRightMostPhraseWord = rightMostIdx === index;
            isLeftMostPhraseWord = leftMostIdx === index;
            isPhraseMiddle = leftMostIdx < index && index < rightMostIdx;
          }
          return (
            <WordHighlighter
              key={word.videoWordId}
              word={word}
              allWords={words}
              position={index}
              highlightPosition={highlightPosition}
              onWordClick={onWordClick}
              onDoubleWordClick={onDoubleWordClick || (() => {})}
              isRightMostPhraseWord={isRightMostPhraseWord}
              isLeftMostPhraseWord={isLeftMostPhraseWord}
              isPhraseMiddle={isPhraseMiddle}
              onPressDown={handlePressDown}
              onPressMove={handlePressMove}
              onPressUp={handlePressUp}
            />
          );
        })}
      </p>
    );
  }
);

export default PlacementTestHighlightedWords; 